<template>
    <div class="max-w-[59rem] rounded-xl shadow-lg mx-auto relative overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-3 bg-yellow-500 py-6 px-4 lg:px-7 relative z-20">
            <div class="space-y-3" v-if="!selectedOption.is_enterprise">
                <div class="flex items-center gap-2" v-if="paymentOption === 'yearly'">
                    <span class="bg-warning-red rounded-full px-2.5 py-1 text-white text-xs font-semibold uppercase"
                        >Save {{ selectedOption.percent_yearly_saving }}</span
                    >
                    <span class="text-purple lg:text-lg line-through"
                        >{{ selectedOption.price_per_month_monthly }} / mo
                        <template v-if="showPricesWithVat">+ VAT</template></span
                    >
                </div>
                <h3 class="text-purple font-semibold flex items-center space-x-2">
                    <span class="text-4xl lg:text-4.5xl font-semibold">
                        {{
                            paymentOption === 'yearly'
                                ? selectedOption.price_per_month_yearly
                                : selectedOption.price_per_month_monthly
                        }}
                        / mo
                    </span>
                    <span v-if="showPricesWithVat" class="text-xl lg:text-1.5xl mt-2"> + VAT </span>
                </h3>
                <span class="text-purple">
                    <template v-if="paymentOption === 'yearly'"> billed annually </template>
                    <template v-else> billed monthly </template>
                </span>
            </div>
            <div v-else>
                <h3 class="text-purple text-4xl font-semibold lg:text-4.5xl">Contact us</h3>
            </div>
            <div class="flex max-sm:flex-wrap items-center gap-3">
                <ToggleSelector class="h-11 max-sm:w-full min-w-60" v-model="paymentOption" :options="paymentOptions" />

                <Multiselect
                    class="smaller-tag max-sm:w-full min-w-60"
                    :allow-empty="false"
                    :searchable="false"
                    v-model="selectedOption"
                    :options="subscriptionProducts"
                    :show-labels="false"
                >
                    <template #singleLabel="{ option }">
                        <span
                            ><strong>{{ option.employee_range }}</strong> employees</span
                        >
                    </template>
                    <template #option="{ option }">
                        <span
                            ><strong>{{ option.employee_range }}</strong> employees</span
                        >
                    </template>
                </Multiselect>
            </div>
        </div>
        <div class="bg-light-gray px-4 lg:px-7">
            <div class="lg:w-[54%] pt-5 pb-7 relative z-10">
                <h4 class="text-purple text-xl sm:text-2xl lg:pr-8 font-semibold">
                    <img src="/img/emojis/emoji-celebration.svg" class="float-left size-6 lg:size-8 me-2" alt="" />
                    Your all-in-one company appreciation platform
                </h4>

                <span
                    class="my-5 text-gold font-bold border border-gold rounded-xl sm:rounded-full max-sm:text-sm px-2 py-1 bg-light-yellow flex"
                >
                    <img
                        src="/img/icon-star-premium.svg"
                        width="16"
                        height="16"
                        class="mr-2 max-sm:self-start max-sm:mt-0.5"
                        alt=""
                    />
                    Includes EVERYTHING in Premium Thankboxes, PLUS…
                </span>

                <ul class="fa-ul icon-bullet-list gold mt-2 sm:text-lg space-y-2">
                    <li v-for="feature in sharedFeatures" :key="feature.text">
                        <i class="fa-solid fa-check-circle fa-li"></i>
                        <span class="ml-2">
                            <span v-html="feature.text" class="mr-2" />
                            <ThankboxInfoTooltip v-if="feature.tooltip" :content="feature.tooltip" />
                        </span>
                    </li>
                </ul>

                <a
                    id="book-demo-button"
                    class="button w-full mt-5"
                    href="/business/book-demo"
                    @click="trackFathomEvent('Subscription contact clicked')"
                >
                    Contact us
                </a>

                <p class="text-sm text-dark-gray italic mt-5">
                    <sup>*</sup>Available to Enterprise (501+ employees) customers
                </p>
            </div>
        </div>

        <picture>
            <source srcset="/img/public-page-assets/woman-smiling-at-phone.webp" type="image/webp" />
            <img
                src="/img/public-page-assets/woman-smiling-at-phone.png"
                alt="Appreciation playbook image"
                width="827"
                height="1200"
                class="absolute max-lg:hidden w-1/2 right-0 bottom-0"
            />
        </picture>
    </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import Multiselect from 'vue-multiselect'
import ToggleSelector from '@/components/ToggleSelector.vue'
import ThankboxInfoTooltip from '@/components/ThankboxInfoTooltip.vue'
import trackFathomEvent from '@/util/trackFathomEvent.ts'

interface SubscriptionProduct {
    id: number
    is_enterprise: boolean
    employee_range: string
    percent_yearly_saving?: string
    price_per_month_monthly?: string
    price_per_month_yearly?: string
}

interface Feature {
    text: string
    tooltip?: string
}

const paymentOptions = [
    {
        value: 'monthly',
        label: 'Monthly',
    },
    {
        value: 'yearly',
        label: 'Yearly',
    },
]

const props = defineProps<{
    showPricesWithVat: boolean
    subscriptionProducts: SubscriptionProduct[]
}>()

// const subscriptionOptions: SubscriptionOption[] = [
//     {
//         id: 1,
//         is_enterprise: false,
//         employee_range: '1-50',
//         percent_yearly_saving: '30%',
//         price_per_month_monthly: '£25',
//         price_per_month_yearly: '£19',
//     },
//     {
//         id: 2,
//         is_enterprise: false,
//         employee_range: '51-100',
//         percent_yearly_saving: '30%',
//         price_per_month_monthly: '£41',
//         price_per_month_yearly: '£30',
//     },
//     {
//         id: 3,
//         is_enterprise: false,
//         employee_range: '101-200',
//         percent_yearly_saving: '30%',
//         price_per_month_monthly: '£81',
//         price_per_month_yearly: '£61',
//     },
//     {
//         id: 4,
//         is_enterprise: false,
//         employee_range: '201-300',
//         percent_yearly_saving: '30%',
//         price_per_month_monthly: '£122',
//         price_per_month_yearly: '£91',
//     },
//     {
//         id: 5,
//         is_enterprise: false,
//         employee_range: '301-400',
//         percent_yearly_saving: '30%',
//         price_per_month_monthly: '£158',
//         price_per_month_yearly: '£118',
//     },
//     {
//         id: 6,
//         is_enterprise: false,
//         employee_range: '401-500',
//         percent_yearly_saving: '30%',
//         price_per_month_monthly: '£190',
//         price_per_month_yearly: '£142',
//     },
//     {
//         id: 7,
//         is_enterprise: true,
//         employee_range: '501+',
//         percent_yearly_saving: '30%',
//         price_per_month_monthly: '',
//         price_per_month_yearly: '',
//     },
// ]

const selectedOption = ref<SubscriptionProduct>(props.subscriptionProducts[0])
const paymentOption = ref<'monthly' | 'yearly'>('yearly')

const sharedFeatures = computed<Feature[]>(() => {
    return [
        {
            text: '<strong>Unlimited</strong> Thankbox use in your team',
            tooltip: 'Everyone can <strong>create and send unlimited</strong> Thankboxes.',
        },
        {
            text: '<strong>Shoutout</strong> Thankboxes',
            tooltip:
                'Great for <strong>monthly wins</strong>, <strong>company events</strong> or <strong>group recognition</strong>.',
        },
        {
            text: 'Company <strong>branding</strong>',
            tooltip: 'Jazz up your Thankbox pages with <strong>your own logo</strong> and <strong>branding</strong>.',
        },
        {
            text: '<strong>Likes & comments</strong>',
            tooltip:
                'Supercharge engagement on each Thankbox with message <strong>emoji reactions & comment threads</strong>.',
        },
        { text: 'Advanced <strong>access controls</strong>' },
        { text: 'Usage <strong>analytics</strong>' },
        { text: '<strong>Single Sign On</strong> (SSO)*' },
        { text: 'Dedicated Thankbox <strong>Success Manager</strong>*' },
        { text: '<strong>14 day free trial</strong>' },
    ]
})
</script>
